import { z } from 'zod';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS-13 Feature 2 — LOYALTY / points (LRU-06).
 *
 * DESIGN (LOCKED, WS13-retention-design.md §Feature 2): points are a NON-MONETARY perk. Earn posts NO
 * money/liability/VAT — it is just a signed-integer-points ledger entry accrued off a receipt's ex-VAT
 * subtotal (or +1 stamp). Redemption converts points→pence and is applied as a STANDARD `discount`
 * receipt line with reasonCode 'loyalty' (already in the discount reason enum), which the VAT engine
 * already nets the normal vat.ts way — so VAT/commission/reporting stay UNCHANGED (ADR-0008 §4: a pure
 * discount never touches gift_card_liability). A member's BALANCE is DERIVED from the append-only ledger
 * (signed sum of `points`), NEVER stored.
 *
 * Points are integers. The ONLY money math is computeDiscount on the server when redeeming — never here.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/**
 * How a salon accrues points. 'points_per_spend' = floor(exVat £) * pointsPerPound per receipt;
 * 'visit_stamps' = +1 stamp per issued receipt (a digital loyalty card; stampsPerReward stamps unlock a
 * rewardValuePence reward).
 */
export const LoyaltyEarnModel = z.enum(['points_per_spend', 'visit_stamps']);
export type LoyaltyEarnModel = z.infer<typeof LoyaltyEarnModel>;

/**
 * The business event a signed-points ledger entry records. 'earn' (accrued off a receipt), 'redeem'
 * (spent → a discount line), 'expiry' (a future expiry job), 'adjustment' (a manual signed credit/debit
 * by a manager, with actor + reason).
 */
export const LoyaltyEventKind = z.enum(['earn', 'redeem', 'expiry', 'adjustment']);
export type LoyaltyEventKind = z.infer<typeof LoyaltyEventKind>;

// ── Program (config — one per salon) ───────────────────────────────────────────

/**
 * A salon's loyalty program config (one per salon). `enabled` false = inert (no earn, no redeem). The
 * earn fields drive accrual; the redeem fields drive the points→pence conversion at the till. NULL
 * `pointsExpireMonths` = points never expire (the expiry job is a schema-ready fast-follow).
 */
export const LoyaltyProgram = z
  .object({
    salonId: Uuid,
    enabled: z.boolean(),
    earnModel: LoyaltyEarnModel,
    /** points_per_spend: points earned per whole pound of ex-VAT spend. */
    pointsPerPound: z.int().min(0),
    /** visit_stamps: how many stamps unlock one reward. */
    stampsPerReward: z.int().min(1),
    /** visit_stamps: the pence value of one unlocked reward. */
    rewardValuePence: Pence,
    /** points_per_spend: pence each point is worth at redemption. */
    redeemPencePerPoint: z.int().min(0),
    /** The minimum points a member must hold to redeem at all (0 = no floor). */
    minRedeemPoints: z.int().min(0),
    /** NULL = points never expire (the expiry job is a fast-follow; the column lands now). */
    pointsExpireMonths: z.int().min(1).nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'LoyaltyProgram' });
export type LoyaltyProgram = z.infer<typeof LoyaltyProgram>;

/** Upsert the per-salon loyalty program config (all fields optional — partial update). Manager+. */
export const UpsertLoyaltyProgram = z
  .object({
    enabled: z.boolean().optional(),
    earnModel: LoyaltyEarnModel.optional(),
    pointsPerPound: z.int().min(0).max(1000).optional(),
    stampsPerReward: z.int().min(1).max(1000).optional(),
    rewardValuePence: Pence.optional(),
    redeemPencePerPoint: z.int().min(0).max(10000).optional(),
    minRedeemPoints: z.int().min(0).max(1_000_000).optional(),
    pointsExpireMonths: z.int().min(1).max(120).nullish(),
  })
  .meta({ id: 'UpsertLoyaltyProgram' });
export type UpsertLoyaltyProgram = z.infer<typeof UpsertLoyaltyProgram>;

// ── Ledger entry (append-only, signed points) ──────────────────────────────────

/**
 * One append-only signed-points ledger entry. `points` is signed (+earn / -redeem) and never zero. The
 * member's balance is the signed sum over their entries — this row is never mutated (a correction is a
 * new 'adjustment' row).
 */
export const LoyaltyLedgerEntry = z
  .object({
    id: Uuid,
    salonId: Uuid,
    clientId: Uuid,
    memberId: Uuid,
    eventKind: LoyaltyEventKind,
    points: z.int().describe('signed: +earn / -redeem (never zero)'),
    receiptId: Uuid.nullable().describe('the source receipt for an earn'),
    discountId: Uuid.nullable().describe('the discount line a redeem produced'),
    reason: z.string().nullable(),
    createdByStaffId: Uuid.nullable().describe('the ACTOR on a redeem/adjustment (audit trail)'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'LoyaltyLedgerEntry' });
export type LoyaltyLedgerEntry = z.infer<typeof LoyaltyLedgerEntry>;

// ── Member (the operator-facing view — derived balance + history) ──────────────

/**
 * The operator's view of one member: the enrolled client, their DERIVED balance (signed sum of the
 * ledger — never a stored column), and their ledger history. Carries the salon-internal ids the console
 * needs; never served on the client-portal surface.
 */
export const LoyaltyMember = z
  .object({
    salonId: Uuid,
    clientId: Uuid,
    enrolled: z.boolean().describe('whether a loyalty_members row exists for this client'),
    /** The DERIVED balance — the signed sum of this client's ledger entries, never stored. */
    balancePoints: z.int(),
    history: z.array(LoyaltyLedgerEntry),
    enrolledAt: IsoDateTime.nullable(),
  })
  .meta({ id: 'LoyaltyMember' });
export type LoyaltyMember = z.infer<typeof LoyaltyMember>;

// ── Requests ───────────────────────────────────────────────────────────────────

/**
 * Redeem points at the till. The server validates (program enabled, balance >= points >=
 * minRedeemPoints), converts points→pence (computeDiscount), writes a standard `discount` line
 * (reasonCode 'loyalty') + a debit ledger entry, all in one tx under a member-row lock. Idempotent on
 * `idempotencyKey` (a retry returns the original). `paymentId`/`appointmentId` link the discount to the
 * supply being reduced (optional — falls back to the points' pence value as the base).
 */
export const RedeemPoints = z
  .object({
    clientId: Uuid,
    points: z.int().min(1).describe('how many points to spend (positive)'),
    paymentId: Uuid.nullish().describe('the till payment the redemption reduces, if any'),
    appointmentId: Uuid.nullish(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'RedeemPoints' });
export type RedeemPoints = z.infer<typeof RedeemPoints>;

/** The result of a redeem: the discount line that was applied + the member's new derived balance. */
export const RedeemPointsResult = z
  .object({
    discount: z.object({
      id: Uuid,
      salonId: Uuid,
      type: z.enum(['percent', 'fixed']),
      basePence: Pence,
      discountPence: Pence,
      reasonCode: z.string(),
      appointmentId: Uuid.nullable(),
      paymentId: Uuid.nullable(),
      createdAt: IsoDateTime,
    }),
    balancePoints: z.int(),
    pointsRedeemed: z.int().min(1),
  })
  .meta({ id: 'RedeemPointsResult' });
export type RedeemPointsResult = z.infer<typeof RedeemPointsResult>;

/**
 * A manual signed points adjustment by a manager (the audit trail — no silent balance edits). `points`
 * is signed (positive credit / negative debit, never zero); `reason` is mandatory. Append-only.
 */
export const AdjustPoints = z
  .object({
    clientId: Uuid,
    points: z
      .int()
      .refine((n) => n !== 0, { message: 'points must be non-zero (a signed credit or debit)' })
      .describe('signed: positive credit, negative debit (never zero)'),
    reason: z.string().trim().min(1).max(500),
    idempotencyKey: z.string().min(8).max(200).optional(),
  })
  .meta({ id: 'AdjustPoints' });
export type AdjustPoints = z.infer<typeof AdjustPoints>;

// ── Client portal DTO (NO internal ids — customer-facing only) ─────────────────

/**
 * The client-portal "Rewards" view for ONE claimed salon. Customer-facing ONLY: NO clientId / memberId /
 * ledger ids / renterId / staffId — the salonName + the points figures + a denormalised recent history.
 */
export const ClientLoyalty = z
  .object({
    salonName: z.string(),
    enabled: z.boolean().describe('whether the salon runs a loyalty program at all'),
    earnModel: LoyaltyEarnModel,
    balancePoints: z.int(),
    /** points_per_spend: pence each point is worth; visit_stamps: the reward value once unlocked. */
    pointValuePence: Pence,
    recent: z.array(
      z.object({
        eventKind: LoyaltyEventKind,
        points: z.int(),
        reason: z.string().nullable(),
        createdAt: IsoDateTime,
      }),
    ),
  })
  .meta({ id: 'ClientLoyalty' });
export type ClientLoyalty = z.infer<typeof ClientLoyalty>;
